import React from 'react';
import { ResponsiveECharts } from './ResponsiveECharts';
import { ChartCard } from '../ChartCard';
import '../studysyncEChartsTheme'; // side effect: registers the 'studysync' theme before render
import { tokens } from '../tokens';
import { ECHARTS_ANIM, cellStagger } from '../motion';
import type { ChartSpec, ChartData } from '../copilotTypes';

type CalendarSpec = Extract<ChartSpec, { chart: 'calendar_heatmap' }>;
type CalendarData = Extract<ChartData, { chart: 'calendar_heatmap' }>;

export function buildCalendarOption(_spec: CalendarSpec, data: CalendarData) {
  const present = data.days.filter((d) => d.value != null) as Array<{ date: string; value: number }>;
  const max = present.reduce((m, d) => Math.max(m, d.value), 1);
  const dates = present.map((d) => d.date).sort();
  const range: [string, string] = [dates[0], dates[dates.length - 1]];

  return {
    ...ECHARTS_ANIM,
    tooltip: {
      // Two-line tooltip mirrors the histogram's: a header line naming the bucket (the date), then a
      // bold measured value (rounded total + metric unit). Keeps both charts' tooltips consistent.
      formatter: (p: any) =>
        `${p.value[0]}<br/><strong>${Math.round(p.value[1]).toLocaleString()}</strong> ${data.unit}`,
    },
    visualMap: {
      min: 0,
      max,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      itemWidth: 12,
      text: ['high', 'low'],
      textStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily },
      inRange: { color: tokens.sequential },
    },
    calendar: {
      range,
      // Auto height lets the 7 week-rows grow to fill the space between `top` and `bottom`, so the
      // grid spans the card instead of leaving a dead band below a fixed-height grid.
      cellSize: ['auto', 'auto'],
      top: 30,
      // Reserve enough room on the left for the day-of-week labels + their margin so the S/M/T/W/T/F/S
      // column is never clipped against the card padding, even on the narrowest (two-up) layout.
      left: 48,
      right: 12,
      // Leave room below the grid for the horizontal visualMap legend (which sits at bottom: 0).
      bottom: 44,
      splitLine: { lineStyle: { color: tokens.surface.border } },
      itemStyle: { color: tokens.surface.page, borderColor: tokens.surface.card, borderWidth: 2 },
      yearLabel: { show: false },
      // Primary text color + medium weight keeps the day labels fully legible (not low-contrast).
      dayLabel: {
        color: tokens.text.primary,
        fontFamily: tokens.fontFamily,
        fontSize: 12,
        fontWeight: 600,
        margin: 10,
      },
      monthLabel: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 11, margin: 8 },
    },
    series: [
      {
        type: 'heatmap',
        coordinateSystem: 'calendar',
        data: present.map((d) => [d.date, d.value]),
        // Cells fade in across the grid; the whole sweep is capped at ≤500 ms no matter how many days
        // are present. `n` is the present-cell count captured here, not read from the ECharts callback.
        animationDelay: cellStagger(present.length),
      },
    ],
  };
}

// memoized so a streaming re-render doesn't rebuild the option -> setOption(notMerge) reflow (see Histogram).
export const CalendarHeatmap = React.memo(function CalendarHeatmap({ title, spec, data }: { title: string; spec: CalendarSpec; data: CalendarData }) {
  const option = React.useMemo(() => buildCalendarOption(spec, data), [spec, data]);
  return (
    <ChartCard title={title} downloadable>
      <ResponsiveECharts theme="studysync" option={option} style={{ height: 280 }} notMerge />
    </ChartCard>
  );
});
